import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Upload, Image, Shield, CheckCircle, XCircle, AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Collapsible Reporting Section Component
const ReportingSection = ({ title, status, url, steps, priority }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-3 border border-gray-300 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
      >
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-black flex items-center gap-2">
            {title}
            {priority && (
              <span className={`text-xs px-2 py-0.5 rounded ${
                priority === 'High' ? 'bg-red-500/20 text-red-700' : 'bg-yellow-500/20 text-yellow-700'
              }`}>
                {priority}
              </span>
            )}
          </h4>
          <p className="text-xs text-gray-600 mt-1">{status}</p>
        </div>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-600" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-600" />
        )}
      </button>

      {isOpen && (
        <div className="px-4 py-4 bg-white border-t border-gray-300">
          <div className="mb-4 pt-2">
            <a
              href={url.startsWith('http') ? url : '#'}
              target={url.startsWith('http') ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              To Report - Click Here
              {url.startsWith('http') && <ExternalLink className="h-4 w-4" />}
            </a>
          </div>

          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-600 mb-2">Steps:</p>
            <ol className="space-y-2">
              {steps.map((step, index) => (
                <li key={index} className="text-xs text-gray-700 flex gap-2">
                  <span className="text-orange-500 font-semibold">{index + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

// URL Parser Function to extract platform and account info
const parseMediaUrl = (url) => {
  if (!url || typeof url !== 'string' || !url.startsWith('http')) {
    return null;
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    const pathname = urlObj.pathname;
    
    let platform = 'Unknown';
    let accountName = 'Unknown';
    let accountUrl = '';
    let postUrl = url;
    
    // Instagram
    if (hostname.includes('instagram.com')) {
      platform = 'Instagram';
      // Try to match username/post pattern first: /username/p/postid or /username/reel/postid
      let instagramMatch = pathname.match(/\/(@?[\w.]+)\/(p|reel|tv)\/([\w-]+)/);
      if (instagramMatch) {
        accountName = instagramMatch[1].replace('@', '');
        accountUrl = `https://instagram.com/${accountName}`;
      } else {
        // Handle direct post links without username: /p/postid or /reel/postid
        const directMatch = pathname.match(/\/(p|reel|tv)\/([\w-]+)/);
        if (directMatch) {
          // Cannot extract username from direct links
          accountName = 'Unknown (direct link)';
          accountUrl = '';
        }
      }
    }
    // YouTube
    else if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      platform = 'YouTube';
      const youtubeMatch = pathname.match(/\/@?([\w-]+)/);
      if (youtubeMatch) {
        accountName = youtubeMatch[1];
        accountUrl = `https://youtube.com/@${accountName}`;
      } else {
        const channelMatch = pathname.match(/\/channel\/([\w-]+)/);
        if (channelMatch) {
          accountName = channelMatch[1];
          accountUrl = `https://youtube.com/channel/${accountName}`;
        }
      }
    }
    // TikTok
    else if (hostname.includes('tiktok.com')) {
      platform = 'TikTok';
      const tiktokMatch = pathname.match(/\/@([\w.]+)/);
      if (tiktokMatch) {
        accountName = tiktokMatch[1];
        accountUrl = `https://tiktok.com/@${accountName}`;
      }
    }
    // Twitter/X
    else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
      platform = 'Twitter/X';
      const twitterMatch = pathname.match(/\/([\w]+)\/status/);
      if (twitterMatch) {
        accountName = twitterMatch[1];
        accountUrl = `https://twitter.com/${accountName}`;
      }
    }
    // Facebook
    else if (hostname.includes('facebook.com') || hostname.includes('fb.com')) {
      platform = 'Facebook';
      const facebookMatch = pathname.match(/\/([\w.]+)\/(posts|videos)/);
      if (facebookMatch) {
        accountName = facebookMatch[1];
        accountUrl = `https://facebook.com/${accountName}`;
      }
    }
    // Reddit
    else if (hostname.includes('reddit.com')) {
      platform = 'Reddit';
      const redditMatch = pathname.match(/\/r\/([\w]+)\/.*\/u\/([\w]+)/);
      if (redditMatch) {
        accountName = `u/${redditMatch[2]} (r/${redditMatch[1]})`;
        accountUrl = `https://reddit.com/user/${redditMatch[2]}`;
      }
    }
    // LinkedIn
    else if (hostname.includes('linkedin.com')) {
      platform = 'LinkedIn';
      const linkedinMatch = pathname.match(/\/in\/([\w-]+)/);
      if (linkedinMatch) {
        accountName = linkedinMatch[1];
        accountUrl = `https://linkedin.com/in/${accountName}`;
      }
    }
    
    return {
      platform,
      accountName,
      accountUrl,
      postUrl
    };
  } catch (error) {
    console.error('URL parsing error:', error);
    return null;
  }
};

// PDF Report Generation Function
const generatePDFReport = async (result, user, mediaInfo, activeTab, uploadedFileName = null) => {
  try {
    const doc = new jsPDF();
    
    // Function to add all content to PDF
    const addContentToPDF = (doc) => {
      let yPosition = 30;
      
      // Title
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Deep Fake Detection Report', 105, yPosition, { align: 'center' });
      yPosition += 5;
      
      // Horizontal line
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 15;
      
      // User Information Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('User Information:', 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Name: ${user?.name || 'N/A'}`, 25, yPosition);
      yPosition += 7;
      doc.text(`Email: ${user?.email || 'N/A'}`, 25, yPosition);
      yPosition += 15;
      
      // Media Analyzed Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Media Analyzed:', 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Type: ${activeTab === 'image' ? 'Image' : 'Video'}`, 25, yPosition);
      yPosition += 7;
      
      // Parse URL if it's a URL
      const urlInfo = parseMediaUrl(mediaInfo);
      
      if (urlInfo) {
        // Platform & Account Information
        doc.setFont('helvetica', 'bold');
        doc.text('Platform Information:', 25, yPosition);
        yPosition += 7;
        
        doc.setFont('helvetica', 'normal');
        doc.text(`Platform: ${urlInfo.platform}`, 30, yPosition);
        yPosition += 7;
        doc.text(`Account: ${urlInfo.accountName}`, 30, yPosition);
        yPosition += 7;
        
        if (urlInfo.accountUrl) {
          doc.setTextColor(0, 0, 255);
          doc.textWithLink(`Account URL: ${urlInfo.accountUrl}`, 30, yPosition, { url: urlInfo.accountUrl });
          doc.setTextColor(0, 0, 0);
          yPosition += 7;
        }
        
        doc.setTextColor(0, 0, 255);
        doc.text('Post URL:', 30, yPosition);
        yPosition += 5;
        const splitPostUrl = doc.splitTextToSize(urlInfo.postUrl, 160);
        doc.textWithLink(splitPostUrl[0], 30, yPosition, { url: urlInfo.postUrl });
        yPosition += splitPostUrl.length * 5;
        doc.setTextColor(0, 0, 0);
      } else {
        // Display filename if available, otherwise generic text
        let mediaText;
        if (uploadedFileName) {
          mediaText = `File: ${uploadedFileName}`;
        } else if (typeof mediaInfo === 'string' && mediaInfo.startsWith('http')) {
          mediaText = `URL: ${mediaInfo}`;
        } else {
          mediaText = `File: ${activeTab === 'image' ? 'Uploaded Image' : 'Uploaded Video'}`;
        }
        const splitMedia = doc.splitTextToSize(mediaText, 165);
        doc.text(splitMedia, 25, yPosition);
        yPosition += splitMedia.length * 7;
      }
      
      yPosition += 8;
      
      // Detection Results Section with Visualization
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Detection Results:', 20, yPosition);
      yPosition += 12;
      
      // Visual Result Indicator (colored box with icon text)
      const confidencePercent = result.confidence * 100;
      let resultColor, resultBgColor, resultText, statusEmoji;
      
      if (result.is_ai && result.confidence > 0.7) {
        resultColor = [220, 38, 38]; // Red
        resultBgColor = [254, 226, 226]; // Light red
        resultText = 'AI - Generated Content';
        statusEmoji = '⚠️';
      } else if (result.is_ai && result.confidence > 0.4) {
        resultColor = [217, 119, 6]; // Yellow/Orange
        resultBgColor = [254, 243, 199]; // Light yellow
        resultText = 'POSSIBLY AI-GENERATED';
        statusEmoji = '⚠';
      } else {
        resultColor = [22, 163, 74]; // Green
        resultBgColor = [220, 252, 231]; // Light green
        resultText = 'AUTHENTIC CONTENT';
        statusEmoji = '✓';
      }
      
      // Draw result box
      doc.setFillColor(resultBgColor[0], resultBgColor[1], resultBgColor[2]);
      doc.roundedRect(20, yPosition, 170, 20, 3, 3, 'F');
      
      // Draw result text
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(resultColor[0], resultColor[1], resultColor[2]);
      doc.text(resultText, 105, yPosition + 13, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      yPosition += 28;
      
      // Confidence Bar Visualization
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Confidence Level:', 25, yPosition);
      doc.text(`${confidencePercent.toFixed(1)}%`, 170, yPosition, { align: 'right' });
      yPosition += 6;
      
      // Draw confidence bar background (gray)
      doc.setFillColor(229, 231, 235);
      doc.roundedRect(25, yPosition, 145, 8, 4, 4, 'F');
      
      // Draw confidence bar fill (colored based on result)
      const barWidth = (confidencePercent / 100) * 145;
      doc.setFillColor(resultColor[0], resultColor[1], resultColor[2]);
      doc.roundedRect(25, yPosition, barWidth, 8, 4, 4, 'F');
      yPosition += 15;
      
      // Detailed metrics
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const isAIText = result.is_ai ? 'YES - AI Generated' : 'NO - Authentic';
      
      doc.text(`AI Generated: ${isAIText}`, 25, yPosition);
      yPosition += 7;
      doc.text(`Similarity Score: ${((result.similarity || 0) * 100).toFixed(2)}%`, 25, yPosition);
      yPosition += 7;
      doc.text(`Processing Time: ${result.processing_time?.toFixed(2) || 'N/A'} seconds`, 25, yPosition);
      yPosition += 7;
      doc.text(`Credits Used: ${result.credits_used || 0}`, 25, yPosition);
      yPosition += 16;
      
      // Platform Credit
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Using Samhara AI for Deepfake Detection', 105, yPosition, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      yPosition += 15;
      
      // Recommendation Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(220, 38, 38);
      doc.text('RECOMMENDATION:', 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const recommendation = 'We strongly recommend that the platform hosting this deepfake media remove it immediately. The individual or account who posted this media should be revoked from the platform either temporarily or permanently. If not, legal action will be taken against both the individual and the platform.';
      const splitRec = doc.splitTextToSize(recommendation, 170);
      doc.text(splitRec, 20, yPosition);
      yPosition += splitRec.length * 5 + 12;
      
      // Declaration Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Declaration:', 20, yPosition);
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const declaration = 'Nerd Indians believes in using technology for good. Please don\'t use technology to abuse or exploit anyone. This report is generated for awareness and protection purposes only.';
      const splitDecl = doc.splitTextToSize(declaration, 170);
      doc.text(splitDecl, 20, yPosition);
      
      // Footer (fixed position at bottom)
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Report Generated: ${new Date().toLocaleString()}`, 105, 285, { align: 'center' });
      doc.text('© 2026 Nerd Nation - All Rights Reserved', 105, 290, { align: 'center' });
      
      return doc;
    };
    
    // Generate PDF
    const completedDoc = addContentToPDF(doc);
    const fileName = `Deepfake_Report_${Date.now()}.pdf`;
    completedDoc.save(fileName);
    toast.success('Report generated successfully!');
    
  } catch (error) {
    console.error('PDF generation error:', error);
    toast.error('Failed to generate report. Please try again.');
  }
};

const SamharaAI = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('image');
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const resultRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (activeTab === 'image') {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
    } else {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a video file');
        return;
      }
    }

    setSelectedFile(file);
    setResult(null);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const analyzeMedia = async () => {
    if (!selectedFile && !mediaUrl.trim()) {
      toast.error(`Please select a ${activeTab} file or enter a URL`);
      return;
    }

    if (!user) {
      toast.error('Please login to use this feature');
      navigate('/');
      return;
    }

    setAnalyzing(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      let response;
      
      if (activeTab === 'image') {
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          response = await axios.post(`${API}/api/samhara-ai/detect-image`, formData, { headers });
        } else {
          response = await axios.post(`${API}/api/samhara-ai/detect-image-url`, {
            image_url: mediaUrl
          }, { headers });
        }
      } else {
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          response = await axios.post(`${API}/api/samhara-ai/detect-video`, formData, { headers });
        } else {
          response = await axios.post(`${API}/api/samhara-ai/detect-video-url`, {
            video_url: mediaUrl
          }, { headers });
        }
      }

      if (response.data.success) {
        setResult(response.data);
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        
        // Show credit usage
        if (response.data.credits_used) {
          toast.success(`Analysis complete! Used ${response.data.credits_used} credits. Remaining: ${response.data.credits_remaining}`);
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Analysis failed';
      toast.error(errorMsg);
      
      // Handle insufficient credits
      if (error.response?.status === 402) {
        setTimeout(() => {
          toast.info('Purchase more credits from the Credits page');
        }, 1500);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setMediaUrl('');
    setResult(null);
    setPreviewUrl(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header */}
      <div className="border-b border-gray-300 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-yellow-600" />
            <span className="text-xl font-bold text-black">Samhara AI</span>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="text-sm text-black font-outfit hover:text-gray-700 flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <h1 className="text-xl sm:text-2xl font-bold gradient-text-gold font-outfit mb-3">
            Deepfake Detector
          </h1>
          <p className="text-base sm:text-lg text-black font-outfit">
            Upload an {activeTab} to instantly check if it was generated by AI.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex gap-2 mb-6 justify-center">
          <button
            onClick={() => { setActiveTab('image'); resetForm(); }}
            data-testid="tab-image"
            className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'image'
                ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-md border-0'
                : 'bg-white text-gray-700 border border-gray-400 hover:border-amber-500 hover:text-amber-400'
            }`}
          >
            Image
          </button>
          <button
            onClick={() => { setActiveTab('video'); resetForm(); }}
            data-testid="tab-video"
            className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'video'
                ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-md border-0'
                : 'bg-white text-gray-700 border border-gray-400 hover:border-amber-500 hover:text-amber-400'
            }`}
          >
            Video
          </button>
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-6 sm:p-8 mb-6">
          <label className="block cursor-pointer">
            <input
              type="file"
              accept={activeTab === 'image' ? 'image/*' : 'video/*'}
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden hover:border-gray-900 transition-colors">
              {previewUrl ? (
                activeTab === 'image' ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-56 sm:h-72 object-contain bg-white" loading="lazy" decoding="async"/>
                ) : (
                  <video
                    src={previewUrl}
                    className="w-full h-56 sm:h-72 object-contain bg-black"
                    controls
                  />
                )
              ) : (
                <div className="p-8 sm:p-12 text-center">
                  <Upload className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-gray-700 mb-4" />
                  <p className="text-base sm:text-lg text-black mb-2">
                    Drop your {activeTab} here or <span className="text-black font-semibold underline">browse</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {activeTab === 'image' ? 'JPG, PNG, WebP' : 'MP4, MOV, AVI'}
                  </p>
                </div>
              )}
            </div>
          </label>

          {/* Upload Options - Browse and Gallery */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <label className="cursor-pointer">
              <input
                type="file"
                accept={activeTab === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="px-4 py-3 border-2 border-gray-300 rounded-lg text-center hover:bg-white hover:border-gray-900 transition-all">
                <Upload className="h-5 w-5 mx-auto mb-1 text-black font-outfit" />
                <span className="text-sm font-medium text-black block">Browse</span>
              </div>
            </label>
            
            <label className="cursor-pointer">
              <input
                type="file"
                accept={activeTab === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="px-4 py-3 border-2 border-gray-300 rounded-lg text-center hover:bg-white hover:border-gray-900 transition-all">
                <Image className="h-5 w-5 mx-auto mb-1 text-black font-outfit" />
                <span className="text-sm font-medium text-black block">Gallery</span>
              </div>
            </label>
          </div>

          {selectedFile && (
            <p className="text-sm text-black font-outfit mt-3 text-center">
              Selected: <span className="font-semibold">{selectedFile.name}</span>
            </p>
          )}

          {/* URL Input */}
          <div className="mt-6">
            <p className="text-sm text-black font-outfit text-center mb-3">or paste a url</p>
            <input
              type="url"
              value={mediaUrl}
              onChange={(e) => { setMediaUrl(e.target.value); setSelectedFile(null); setResult(null); }}
              placeholder={`Direct ${activeTab} URLs only (right click > Copy ${activeTab} address)`}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Analyze Button */}
        <Button
          onClick={analyzeMedia}
          disabled={analyzing || (!selectedFile && !mediaUrl.trim())}
          data-testid="analyze-button"
          className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white py-5 sm:py-6 text-base sm:text-lg font-semibold rounded-xl disabled:opacity-50 border-0 shadow-md transition-all duration-200"
        >
          {analyzing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
              Analyzing...
            </>
          ) : (
            'Analyze'
          )}
        </Button>

        {/* Results */}
        {result && (
          <div ref={resultRef} className="mt-8 p-6 sm:p-8 bg-white rounded-2xl border-2 border-gray-300">
            <div className="text-center mb-6">
              {result.is_ai && result.confidence > 0.7 ? (
                <>
                  <XCircle className="h-16 w-16 sm:h-20 sm:w-20 text-red-600 mx-auto mb-4" />
                  <h3 className="text-2xl sm:text-3xl font-bold text-red-600 mb-2">
                    AI-Generated Content
                  </h3>
                  <p className="text-black font-outfit">This appears to be synthetically generated</p>
                </>
              ) : result.is_ai && result.confidence > 0.4 ? (
                <>
                  <AlertTriangle className="h-16 w-16 sm:h-20 sm:w-20 text-yellow-600 mx-auto mb-4" />
                  <h3 className="text-2xl sm:text-3xl font-bold text-yellow-600 mb-2">
                    Possibly AI-Generated
                  </h3>
                  <p className="text-black font-outfit">Manual review recommended</p>
                </>
              ) : (
                <>
                  <CheckCircle className="h-16 w-16 sm:h-20 sm:w-20 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">
                    Authentic Content
                  </h3>
                  <p className="text-black font-outfit">This appears to be real</p>
                </>
              )}
            </div>

            {/* Confidence Bar */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-black">Confidence</span>
                <span className="text-lg font-bold text-black">
                  {(result.confidence * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    result.is_ai && result.confidence > 0.7
                      ? 'bg-red-600'
                      : result.is_ai && result.confidence > 0.4
                      ? 'bg-yellow-600'
                      : 'bg-green-600'
                  }`}
                  style={{ width: `${result.confidence * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-4 bg-white rounded-lg border border-gray-300">
                <p className="text-xs text-gray-500 mb-1">Result</p>
                <p className={`text-sm font-semibold ${result.is_ai ? 'text-red-600' : 'text-green-600'}`}>
                  {result.is_ai ? 'AI-Generated' : 'Authentic'}
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-gray-300">
                <p className="text-xs text-gray-500 mb-1">Similarity Score</p>
                <p className="text-sm font-semibold text-black">
                  {result.similarity !== undefined ? `${(result.similarity * 100).toFixed(1)}%` : 'N/A'}
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-gray-300">
                <p className="text-xs text-gray-500 mb-1">Processing Time</p>
                <p className="text-sm font-semibold text-black">
                  {result.processing_time ? `${result.processing_time.toFixed(2)}s` : 'N/A'}
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-gray-300">
                <p className="text-xs text-gray-500 mb-1">Credits Used</p>
                <p className="text-sm font-semibold text-black">
                  {result.credits_used || 0}
                </p>
              </div>
            </div>

            {/* Reset Button */}
            <Button
              onClick={resetForm}
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white py-5 text-base font-semibold rounded-xl border-0 shadow-md transition-all duration-200"
            >
              Analyze Another
            </Button>

            {/* Generate Report Button */}
            <Button
              onClick={() => generatePDFReport(result, user, previewUrl || mediaUrl, activeTab, selectedFile?.name)}
              className="w-full mt-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-5 text-base font-semibold rounded-xl border-0 shadow-md transition-all duration-200"
            >
              📄 Generate Report
            </Button>

            {/* Reporting Guide Section */}
            <div className="mt-8 p-6 bg-white rounded-xl border border-gray-300">
              <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-500" />
                Platform Reporting Guide
              </h3>
              <p className="text-sm text-gray-700 mb-6">
                If you detected deepfake or manipulated content, report it using these official channels:
              </p>

              {/* Collapsible Sections */}
              <ReportingSection
                title="Instagram / Facebook"
                status="✅ Direct Reporting"
                url="https://help.instagram.com/contact/504521742987441"
                steps={[
                  "Go to the content and tap the three dots menu",
                  "Select 'Report' and choose the appropriate violation",
                  "For NCII, select 'Nudity or sexual activity' > 'Sharing private images'",
                  "For impersonation, select 'Pretending to be someone else'",
                  "Provide additional context if prompted"
                ]}
              />

              <ReportingSection
                title="YouTube"
                status="✅ Direct Reporting"
                url="https://support.google.com/youtube/answer/2802027"
                steps={[
                  "Click the three dots below the video",
                  "Select 'Report'",
                  "Choose 'Spam or misleading' > 'Impersonation' for fake accounts",
                  "For manipulated media, choose 'Harmful or dangerous acts'",
                  "Include URLs and timestamps in your description"
                ]}
              />

              <ReportingSection
                title="Twitter / X"
                status="✅ Direct Reporting"
                url="https://help.twitter.com/forms/private_information"
                priority="High"
                steps={[
                  "Navigate to the tweet/profile you want to report",
                  "Click the three dots (...) menu",
                  "Select 'Report post/account'",
                  "Choose 'It's abusive or harmful' or 'Sharing private information'",
                  "Follow the prompts and submit"
                ]}
              />

              <ReportingSection
                title="TikTok"
                status="✅ Direct Reporting"
                url="https://www.tiktok.com/legal/report/privacy"
                priority="High"
                steps={[
                  "Tap and hold on the video",
                  "Select 'Report'",
                  "Choose 'Impersonation', 'Privacy violation', or 'Harmful misinformation'",
                  "Provide details about why you're reporting",
                  "Submit the report"
                ]}
              />

              <ReportingSection
                title="Reddit"
                status="✅ Direct Reporting"
                url="https://www.reddit.com/report"
                priority="High"
                steps={[
                  "Click 'Report' under the post/comment",
                  "Select 'It breaks Reddit's rules'",
                  "Choose 'Sharing personal information' or 'Impersonation'",
                  "Add context if necessary",
                  "Submit"
                ]}
              />

              <ReportingSection
                title="Google Search Removal"
                status="✅ Content Removal Tool"
                url="https://support.google.com/websearch/troubleshooter/9685456"
                steps={[
                  "Go to Google's content removal tool",
                  "Select 'Remove information about you'",
                  "Choose the appropriate category (NCII, personal info)",
                  "Provide URLs of content in search results",
                  "Google will review and remove from search index"
                ]}
              />

              <ReportingSection
                title="Adult/Porn Sites"
                status="⚠️ Generic Process"
                url="Contact site's abuse/DMCA form"
                steps={[
                  "Find the site's 'Report Abuse' or 'DMCA' link (usually in footer)",
                  "Submit a formal DMCA takedown notice",
                  "Include: your contact info, content URLs, statement of ownership",
                  "State that content was shared without consent",
                  "Consider using StopNCII.org for hash-based removal"
                ]}
              />

              <ReportingSection
                title="Snapchat"
                status="✅ Support Form"
                url="https://support.snapchat.com/en-US/i-need-help"
                priority="Medium"
                steps={[
                  "Go to Snapchat support",
                  "Select 'Safety & Abuse' category",
                  "Choose 'Report abuse or safety concern'",
                  "Fill out the form with details",
                  "Submit for review"
                ]}
              />

              <ReportingSection
                title="LinkedIn"
                status="✅ Direct Reporting"
                url="https://www.linkedin.com/help/linkedin/ask/TS-NFPI"
                priority="Medium"
                steps={[
                  "Visit the profile or post to report",
                  "Click the three dots menu",
                  "Select 'Report'",
                  "Choose 'Fake profile' or 'Harassment'",
                  "Submit with details"
                ]}
              />

              <ReportingSection
                title="Telegram"
                status="✅ Report via Bot"
                url="https://telegram.org/faq#q-how-do-i-report-a-channel"
                priority="Medium"
                steps={[
                  "Open the channel/chat",
                  "Tap the channel name at the top",
                  "Tap 'Report'",
                  "Select the reason (spam, violence, etc.)",
                  "Confirm the report"
                ]}
              />

              <ReportingSection
                title="Discord"
                status="✅ Trust & Safety"
                url="https://dis.gd/request"
                priority="Medium"
                steps={[
                  "Right-click the message/user",
                  "Select 'Report'",
                  "Or visit Discord's Trust & Safety form",
                  "Provide user ID, server ID, and message links",
                  "Describe the violation"
                ]}
              />

              {/* Critical Resources */}
              <div className="mt-6 p-4 bg-red-50 border border-red-300 rounded-lg">
                <h4 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Critical Resources
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <a 
                      href="https://stopncii.org" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-orange-400 hover:text-orange-300 underline"
                    >
                      🔴 StopNCII.org
                    </a>
                    <p className="text-xs text-gray-700 mt-1">
                      Hash-based removal service for non-consensual intimate images (NCII) across major platforms.
                    </p>
                  </div>

                  <div>
                    <a 
                      href="https://www.missingkids.org/gethelpnow/cybertipline" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-orange-400 hover:text-orange-300 underline"
                    >
                      🔴 NCMEC CyberTipline
                    </a>
                    <p className="text-xs text-gray-700 mt-1">
                      Report child sexual abuse material (CSAM) - Critical for minors.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              <span className="font-semibold">Important Note:</span> Our App is only 95% accurate in detecting deepfakes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SamharaAI;

